import type { ControlledIFrameElement } from '@fullstory/controlled-iframe';
import { FsEvent } from '@fullstory/fs-script-api';
import {
  CssRuleIdKind,
  RawEvent,
  RawEventKind,
  RecFeature,
  SheetOwner,
  SheetQualifiedIdentity,
  TimeStampMs,
} from '@fullstory/recording-api';
import {
  FramePath,
  NodePath,
  ObjectCollection,
  ObjectInfo,
  ObjectPathDepth,
  ObjectQueryOpts,
  PlaybackContext,
  RendererCursor,
} from '@fullstory/renderer-shared';
import type { ResourcePreloader } from '@fullstory/resource-preloader';
import { AttrMasked } from '@fullstory/shared/blockattrs';
import { maskText, wasMasked } from '@fullstory/shared/masktext';
import { decodeNodeTree } from '@fullstory/shared/node_decoder';
import type { LayoutViewportSize, Size } from '@fullstory/shared/viewport';
import { assertExhaustive, isNonNull, notAwaited } from '@fullstory/typeutils';
import { shorten } from '@fullstory/util/fmt';
import type { Point, Vec2D } from '@fullstory/util/geometry';
import { WorkQueueProcessor } from '@fullstory/util/workqueue';
import type { FramePoint } from './events';
import { addRawEventToTimeline, PageEventTimeline } from './events';
import { FramePlaybackHost, LoadResult, MouseClickTouchId } from './frame_playback_host';
import { FsCanvas } from './fsdom/fscanvas';
import type { FsCSSOMSheet, FSRuleContainer } from './fsdom/fscssom';
import { FsCSSOMElement } from './fsdom/fscssomelement';
import { FSDialogState, getDialogState } from './fsdom/fsdialog';
import type { FsDocument, FsRootDocument } from './fsdom/fsdocument';
import { FsDocumentOrShadowRoot } from './fsdom/fsdocumentorshadowroot';
import type { FsDomEventHandlers } from './fsdom/fsdomeventhandlers';
import type { FsNode } from './fsdom/fsnode';
import { FsRootDocumentContainer } from './fsdom/fsrootdocumentcontainer';

const PRELOAD_HORIZON_MS = 60e3; // How far in advance we should preload resources.
const SEEK_THRESHOLD_MS = 1000; // Threshold in millis, that constitutes a "seek".
const ReifyNodeLimit = 1_500_000; // Protect playback from OOMs due to large dom trees

export interface FramePlaybackConfig {
  readonly ctx: PlaybackContext;
  readonly container: HTMLDivElement;
  readonly host: FramePlaybackHost;
  readonly preloader: ResourcePreloader;
  readonly orgId: string;
  readonly windowSize: Size;
  readonly screenSize: Size;
}

export class FramePlayback implements FsDomEventHandlers {
  private readonly _ctx: PlaybackContext;
  private readonly _container: HTMLDivElement;
  private readonly _host: FramePlaybackHost;
  private readonly _preloader: ResourcePreloader;
  private readonly _windowSize: Size;
  private readonly _screenSize: Size;

  private _rootDocContainer: FsRootDocumentContainer;

  /**
   * The timestamp of the first MUT_INSERT into the initial root document, or
   * undefined if one hasn't been encountered yet.
   */
  private _firstInsertMs: TimeStampMs | undefined = undefined;

  // Set to true when seeking (rather than playing normally).
  private _seeking = false;

  // The latest we have played up to.
  private _highWaterMark = 0;

  // The time when we transitioned into an idle state.
  private _idleStartTimeMs: number | undefined;

  // A handle to our rAF callback, or zero if it's not running.
  private _rafHandle: number = 0;

  // A promise used to ensure that all changes introduced by a playToTime() call
  // are committed before we process the next playToTime() call. This is needed
  // to handle asynchronous operations during playback.
  private _playbackCommitted: Promise<void>;

  // The group of events passed to each call to playToTime() should commit
  // completely before the next group of events begins. To help detect bugs
  // where event groups are reordered or interleaved, we assign each group a
  // monotonically increasing id for use and track the order in which the groups
  // commit.
  private _nextEventGroupId: number = 0;
  private _lastStartedEventGroupId: number | undefined = undefined;
  private _lastCommittedEventGroupId: number | undefined = undefined;

  // Used to signal that we're being torn down and we should stop playback.
  private _abortController: AbortController;

  private _pageEventTimeline = new PageEventTimeline();

  private readonly _rawEventWorkQueueProcessor = new WorkQueueProcessor<RawEvent>(
    'RawEvent work queue',
    (evt: RawEvent) => {
      addRawEventToTimeline(this._pageEventTimeline, evt);
    },
  );

  private _currentFullscreen: NodePath | null = null;

  /** If set, a cursor to be displayed instead of the captured cursor. */
  #cursorOverride: Exclude<RendererCursor, 'captured'> | null = null;

  /** A map from frames to the documents they currently contain. */
  #frameDocs = new Map<FramePath, FsDocument>();

  constructor(config: FramePlaybackConfig) {
    this._ctx = config.ctx;
    this._container = config.container;
    this._host = config.host;
    this._preloader = config.preloader;
    this._windowSize = config.windowSize;
    this._screenSize = config.screenSize;
    this._abortController = new AbortController();
    this._rootDocContainer = new FsRootDocumentContainer(this._ctx, config.container, this);
    this._playbackCommitted = Promise.resolve();
  }

  // Returns the container element which we're rendering into. This is owned by
  // the code that created this FramePlayback object.
  container(): HTMLDivElement {
    return this._container;
  }

  // Returns the <iframe> associated with the active root document. This is
  // owned by FramePlayback. Note that the active root document may change over
  // time, and there may not be an <iframe> associated with it if playback
  // hasn't started yet or we're playing in buffered mode.
  activeRootDocFrame(): ControlledIFrameElement | null {
    return this._rootDocContainer.frame;
  }

  activeFsRootDocument(): FsRootDocument | null {
    const fsDocumentRoot = this._rootDocContainer.getContentDocument();
    return fsDocumentRoot || null;
  }

  /**
   * @returns the timestamp of the first MUT_INSERT into the initial root
   * document, or undefined if one hasn't been encountered yet.
   */
  firstInsertMs(): TimeStampMs | undefined {
    if (this._firstInsertMs !== undefined) {
      return this._firstInsertMs;
    }

    const getInitialRootDocFirstInsertMs = () => {
      const initialRootDoc = this._pageEventTimeline.rootDocument.atOrAfter(0);
      if (!initialRootDoc) {
        return undefined;
      }
      const firstInsertMs = initialRootDoc.firstInsertMs;
      if (firstInsertMs === Number.POSITIVE_INFINITY) {
        return undefined;
      }
      return firstInsertMs;
    };

    // See if we already found the first MUT_INSERT.
    this._firstInsertMs = getInitialRootDocFirstInsertMs();
    if (this._firstInsertMs !== undefined) {
      return this._firstInsertMs;
    }

    // If not, try to find it by synchronously processing raw events.
    this._rawEventWorkQueueProcessor.processSyncWhile(
      event => event.Kind !== RawEventKind.MUT_INSERT,
    );

    this._firstInsertMs = getInitialRootDocFirstInsertMs();
    return this._firstInsertMs;
  }

  // Scan all events and do stuff like segregating by frame ID and ensuring monotonicity.
  // We can tolerate a linear scan so long as the number of raw events per page
  // stays below 10^4 events +- an order of magnitude. Which shouldn't be a problem.
  appendRawEvents(events: RawEvent[]): void {
    this._rawEventWorkQueueProcessor.addWork(events);
    this._rawEventWorkQueueProcessor.processAsync();
  }

  // Renders the playback for a given page up to the pageOffset.
  async playToTime(pageOffset: number, requestId?: number | string) {
    // Don't queue up any new work if we've been aborted.
    if (!this._abortController.signal.aborted) {
      const ready = this._playbackCommitted;
      this._playbackCommitted = this._playToTimeWhenReady(ready, pageOffset);
      await this._playbackCommitted;
    }

    // TODO(jordanhurt) - we can remove this check and always await
    // if the feature flag rollout improves performance across orgs
    if (this._ctx.flags.features.AppPlaybackMinimizeFouc || requestId !== undefined) {
      await this._playbackCommitted;
      await this._documentSettled();
    }

    // If the caller requested it, send them a notification when we finish
    // processing this playToTime() request. Note that even if we've been
    // aborted, we may still have pending work in the queue, so we want to wait
    // for _playbackCommitted either way.
    if (requestId !== undefined) {
      this._host.playToTimeCommitted(requestId);
    }
  }

  /**
   * Play to the inital timestamp. We do this while playback is hidden to
   * avoid FOUC and kick off resource preloading. By default, we play to the
   * first visible content on the page, but a different offset can be specified
   * to seek to a later timestamp. That allows us to avoid FOUC even for
   * sessions which deep link into the middle of a page.
   */
  async playToInitialTimeStamp(requestedTimeStampMs?: TimeStampMs): Promise<void> {
    this.beginSeek();
    const timeStampMs = requestedTimeStampMs ?? this.firstInsertMs() ?? 0;
    await this.playToTime(timeStampMs, 'playToInitialTimeStamp');
  }

  /** Pause any active CSS animations. */
  onPause(): void {
    const rootDoc = this._rootDocContainer.getContentDocument();

    if (!rootDoc) {
      return;
    }

    rootDoc.setPaused(true);
  }

  private async _playToTimeWhenReady(
    ready: Promise<void>,
    requestedPageOffsetMs: TimeStampMs,
  ): Promise<void> {
    const eventGroupId = this._nextEventGroupId++;

    try {
      await ready;
    } catch (e) {
      console.log(`playToTimeWhenReady: Unexpected exception waiting for ready: ${e}`);
    }

    if (
      this._lastStartedEventGroupId !== undefined &&
      this._lastStartedEventGroupId !== eventGroupId - 1
    ) {
      console.warn(
        `Starting event group ${eventGroupId}, but the last event group we started ` +
          `was ${this._lastStartedEventGroupId}. Event groups are misordered.`,
      );
    }
    if (
      this._lastCommittedEventGroupId !== undefined &&
      this._lastCommittedEventGroupId !== eventGroupId - 1
    ) {
      console.warn(
        `Starting event group ${eventGroupId}, but the last event group we committed ` +
          `was ${this._lastCommittedEventGroupId}. Event groups are misordered.`,
      );
    }
    this._lastStartedEventGroupId = eventGroupId;

    // If we've been aborted, reject the promise.
    if (this._abortController.signal.aborted) {
      throw new Error('Playback aborted');
    }

    // Don't seek before the first insert in the initial root document. This
    // ensures that a buggy caller doesn't rewind us before the point that
    // playToFirstVisibleContent() seeked us to at playback start.
    const pageOffsetMs = Math.max(requestedPageOffsetMs, this.firstInsertMs() ?? 0);

    // Synchronously process raw events up to the current time.
    this._rawEventWorkQueueProcessor.processSyncWhile(e => e.When <= pageOffsetMs);

    // Content may change at any time when we're actively playing.
    this.onRenderingMayChange();

    // Clear the frame-to-doc mapping; we'll reconstruct it as we play events.
    this.#frameDocs.clear();

    const rootDocEvents = this._pageEventTimeline.rootDocument.at(pageOffsetMs);
    if (rootDocEvents) {
      // Reset to the beginning and reinitialize if we're seeking backwards.
      if (pageOffsetMs < this._highWaterMark) {
        this._rootDocContainer.setContentDocument(undefined);
      }

      const rootDoc = this._rootDocContainer.setContentDocument(rootDocEvents);
      await this._rootDocContainer.flush();

      await this._playDocumentToTime(rootDoc, pageOffsetMs);

      // After all frames are done, interpolate mouse moves, et al.
      try {
        this._playEventsFromPageTimeline(pageOffsetMs);
      } catch (e) {
        console.log(`Unexpected exception playing page timeline events: ${e}`);
      }

      // Reenable CSS animations if we previously paused them.
      try {
        rootDoc.setPaused(false);
      } catch (e) {
        console.log(`Unexpected exception unpausing CSS animations: ${e}`);
      }
    } else {
      this._rootDocContainer.setContentDocument(undefined);
      await this._rootDocContainer.flush();
    }

    notAwaited(this.endSeek());
    this._highWaterMark = pageOffsetMs;

    if (this._lastStartedEventGroupId !== eventGroupId) {
      console.warn(
        `Committing event group ${eventGroupId}, but we've already started event ` +
          `group ${this._lastStartedEventGroupId}. Event groups are misordered.`,
      );
    }
    if (
      this._lastCommittedEventGroupId !== undefined &&
      this._lastCommittedEventGroupId !== eventGroupId - 1
    ) {
      console.warn(
        `Committing event group ${eventGroupId}, but the last event group we committed ` +
          `was ${this._lastCommittedEventGroupId}. Event groups are misordered.`,
      );
    }
    this._lastCommittedEventGroupId = eventGroupId;
  }

  private beginSeek() {
    if (this._seeking) {
      return;
    }
    this._seeking = true;
    this._host.hide();
  }

  private async endSeek(): Promise<void> {
    if (!this._seeking) {
      return;
    }
    this._seeking = false;

    await this._documentSettled();
    this._host.show();
    this.onRenderingMayChange();
  }

  private async _documentSettled(): Promise<void> {
    const rootDoc = this._rootDocContainer.getContentDocument();
    if (!rootDoc) {
      return;
    }
    await rootDoc.loadingResources(this._ctx.maxResourceWait);
    this._syncRenderingChanges();
  }

  private reifyDom(
    doc: FsDocument,
    parentNode: FsNode | null,
    beforeNode: FsNode | null,
    compressed: any[],
  ): void {
    const nodeCount = doc.byId.length;
    if (this._ctx.flags.features.AppPlaybackReifyNodeLimit && nodeCount >= ReifyNodeLimit) {
      console.warn(
        `Too many dom nodes found: ${nodeCount} limit: ${ReifyNodeLimit}. Skipping dom reification.`,
      );
      return;
    }

    const stream = doc.lz.decode(compressed);
    decodeNodeTree(doc.builder, stream, parentNode, beforeNode);
  }

  private _playEvent(doc: FsDocument, evt: RawEvent): void {
    if (!doc) {
      console.log('No document. Aborting playing event: ', evt);
      return;
    }

    switch (evt.Kind) {
      case RawEventKind.MUT_INSERT: {
        const parentId = evt.Args[0];
        const parentNode = parentId >= 0 ? doc.byId[parentId] : null;
        if (parentId == -1 || parentNode != null) {
          const nextId = evt.Args[1];
          const insertBefore = nextId == -1 ? null : doc.byId[nextId];
          this.reifyDom(doc, parentNode, insertBefore, evt.Args[2]);
        } else {
          console.log(`MUT_INSERT: Unexpectedly missing target node ${evt.Args[0]}`);
        }
        break;
      }
      case RawEventKind.MUT_REMOVE: {
        for (let i = 0; i < evt.Args.length; ++i) {
          doc.removeNode(evt.Args[i]);
        }
        break;
      }
      case RawEventKind.MUT_ATTR: {
        const node = doc.byId[evt.Args[0]];
        if (node) {
          const attr = evt.Args[1];
          if (
            attr === 'value' &&
            (node.tag === 'input' || node.tag === 'textarea' || node.tag === 'select')
          ) {
            const value = getTextCorpusReplacement(evt.Args[2], this._ctx.page.textCorpus);
            node.setAttr(attr, value);
          } else {
            node.setAttr(attr, evt.Args[2]);
          }
        } else {
          console.log(`MUT_ATTR: Unexpectedly missing target node ${evt.Args[0]}`);
        }
        break;
      }
      case RawEventKind.MUT_TEXT: {
        const node = doc.byId[evt.Args[0]];
        if (!node) {
          console.warn(`MUT_TEXT: Missing target node ${evt.Args[0]}`);
        } else if (!node.isText()) {
          console.warn(`MUT_TEXT: Wrong node type for target node ${node}`);
        } else {
          let value = getTextCorpusReplacement(evt.Args[1], this._ctx.page.textCorpus);
          value = ensureMasked(node, value);
          node.setText(value);
        }
        break;
      }
      case RawEventKind.MUT_SHADOW: {
        const parentId = evt.Args[0];
        const parentNode = evt.Args[0] >= 0 ? doc.byId[parentId] : null;
        if (parentNode != null) {
          this.reifyDom(doc, parentNode, null, evt.Args[1]);
        } else {
          console.log(`MUT_SHADOW: Unexpectedly missing target node ${evt.Args[0]}`);
        }
        break;
      }

      case RawEventKind.VALUECHANGE: {
        const node = doc.byId[evt.Args[0]];
        let value = getTextCorpusReplacement(evt.Args[1], this._ctx.page.textCorpus);
        value = ensureMasked(node, value);
        node.setValue(value);
        break;
      }

      case RawEventKind.ELEMENT_PROP: {
        const node = doc.byId[evt.Args[0]];
        node.setProp(evt.Args[1], evt.Args[2]);
        break;
      }

      case RawEventKind.PLACEHOLDER_SIZE: {
        const node = doc.byId[evt.Args[0]];
        if (!node) {
          console.warn(`PLACEHOLDER_SIZE: Missing target node ${evt.Args[0]}`);
        } else {
          node.setPlaceholderSize(evt.Args[1], evt.Args[2]);
        }
        break;
      }

      case RawEventKind.PLAY:
      case RawEventKind.PAUSE: {
        const node = doc.byId[evt.Args[0]];
        if (!node) {
          console.warn(`PLAY/PAUSE: Missing target node ${evt.Args[0]}`);
        } else if (!node.isMediaElement()) {
          console.warn(`PLAY/PAUSE: Wrong node type for target node ${node}`);
        } else if (evt.Kind == RawEventKind.PLAY) {
          node.play();
        } else {
          node.pause();
        }
        break;
      }

      case RawEventKind.ADOPTED_STYLESHEETS: {
        const [nodeId, sheetIds] = evt.Args;
        const node = doc.byId[nodeId];
        if (!node) {
          console.warn(`ADOPTED_STYLESHEETS: Missing target node ${evt.Args[0]}`);
        } else if (!(node instanceof FsDocumentOrShadowRoot)) {
          console.warn(`ADOPTED_STYLESHEETS: Wrong node type for target node ${node}`);
        } else {
          node.setAdoptedStyleSheets(sheetIds);
        }
        break;
      }
      case RawEventKind.CUSTOM_ELEMENT_DEFINED: {
        doc.defineCustomElement(evt.Args[0]);
        break;
      }
      case RawEventKind.CSSRULE_INSERT: {
        const [id, ruleOrRules, maybeStartIdx, maybePathToRule] = evt.Args;
        const rules = typeof ruleOrRules === 'string' ? [ruleOrRules] : ruleOrRules;

        const sheet = getTargetCssSheet(doc, id);

        let target: FSRuleContainer = sheet;
        if (maybePathToRule) {
          target = sheet.getGroupByPath(maybePathToRule);
        }

        const startIdx = maybeStartIdx ?? 0;
        for (let i = 0; i < rules.length; i++) {
          const idx = startIdx + i;
          const cssText = rules[i];
          target.insertRule(cssText, idx);
        }
        break;
      }

      case RawEventKind.RESET_CSS_SHEET: {
        const [id] = evt.Args;
        const sheet = getTargetCssSheet(doc, id);

        sheet.reset();
        break;
      }

      case RawEventKind.CSSRULE_DELETE: {
        const [id, index, maybePathToRule] = evt.Args;
        const sheet = getTargetCssSheet(doc, id);
        const removeIndex = index ?? 0;

        let target: FSRuleContainer = sheet;
        if (maybePathToRule) {
          target = sheet.getGroupByPath(maybePathToRule);
        }
        target.deleteRule(removeIndex);
        break;
      }

      case RawEventKind.CSSRULE_UPDATE: {
        const [id, updates] = evt.Args;
        const type = id[0];

        if (type === CssRuleIdKind.Cached) {
          const ruleId = id[1] as number;
          doc.ruleOwners.get(ruleId)?.updateRuleById(updates, ruleId);
          break;
        }

        const [_, sheetId, index, maybePathToRule] = id;

        if (!sheetId) {
          console.warn('Expected sheet ID when getting CSSRULE_UPDATE');
          break;
        }

        if (index === undefined) {
          console.warn('Index should have been provided when getting CSSRULE_UPDATE');
          break;
        }

        // We have not seen this rule before, and need to get it from the sheet before we can update it
        // NOTE: maybePathToRule isn't popped to remove the rule index from the path since we are explicitly
        // in a "new" rule path and this path expects the rule will be created.
        getTargetCssSheet(doc, sheetId)?.updateRuleByIndices(updates, index, maybePathToRule ?? []);

        break;
      }

      case RawEventKind.DISABLE_STYLESHEET: {
        const [id, disablement] = evt.Args;
        const sheet = getTargetCssSheet(doc, id);
        sheet.disabled = disablement;
        break;
      }

      case RawEventKind.CANVAS_DETACHED_DIMENSION: {
        const [detachedId, width, height] = evt.Args;
        const { id } = detachedId;
        const node = doc.maybeCreateDetachedCanvasById(id);
        node.setAttr('width', width.toString());
        node.setAttr('height', height.toString());
        break;
      }
      case RawEventKind.CANVAS: {
        const id = evt.Args[0];
        if (typeof id === 'number') {
          // fs id
          const node = doc.byId[id];
          if (node && node instanceof FsCanvas) {
            node.event(evt);
          } else {
            console.warn(
              `Attempted to apply a canvas operation to node fsid ${evt.Args[0]} that isn't canvas`,
            );
          }
        } else {
          // detached id
          const detachedId = id.id;
          const detachedCanvas = doc.maybeCreateDetachedCanvasById(detachedId);
          detachedCanvas.event(evt);
        }
        break;
      }
      case RawEventKind.REC_FEAT_SUPPORTED: {
        for (let i = 0; i < evt.Args.length; i += 2) {
          const feature = evt.Args[i] as RecFeature;
          const on = evt.Args[i + 1] as boolean;

          doc.recFeatures.set(feature, on);
        }
        break;
      }
      case RawEventKind.DEFERRED_RESOLVED: {
        const node = doc.byId[evt.Args[0]];
        if (!node) {
          console.warn(`Missing deferred target node ${evt.Args[0]}`);
        } else {
          node.resolveDeferred();
        }
        doc.deferredIds = doc.deferredIds.filter(id => id !== evt.Args[0]);
        break;
      }
      case RawEventKind.DIALOG: {
        const dialogId = evt.Args[0];
        const dialog = doc.dialogsById(dialogId) ?? doc.createDialog(dialogId);
        const state = getDialogState(evt);
        if (state) {
          dialog.setState(state);
        }
        dialog.setIsModal(!!evt.Args[2]);
        break;
      }
    }
  }

  private _playEventsFromPageTimeline(pageOffset: number): void {
    // Mouse:
    const mousePos = this.#framePointToPage(this._pageEventTimeline.mousePosition.at(pageOffset));
    if (mousePos) {
      const pointerMode = this._pageEventTimeline.pointerMode.at(pageOffset) ?? 'mouse-and-touch';
      if (pointerMode === 'mouse-and-touch') {
        this._positionPointer(mousePos);
      }

      const mouseDown = this._pageEventTimeline.mouseDown.at(pageOffset) ?? false;
      if (mouseDown) {
        this._touchDown(MouseClickTouchId, mousePos);
      } else {
        this._touchUp(MouseClickTouchId);
      }
    }

    // Touch:
    const touchActive = this._pageEventTimeline.touchActive;
    const touchPosition = this._pageEventTimeline.touchPosition;
    for (const [touchId, touchIdIsActive] of touchActive) {
      if (!touchIdIsActive.at(pageOffset)) {
        this._touchUp(touchId);
        continue; // Not currently touching.
      }

      const pos = this.#framePointToPage(touchPosition.get(touchId).at(pageOffset));
      if (!pos) {
        this._touchUp(touchId);
        continue; // Position is unknown or relative to an unknown frame.
      }

      this._touchDown(touchId, pos);
    }

    const fullscreen = this._pageEventTimeline.fullscreen.at(pageOffset);

    if (fullscreen) {
      let node: FsNode | undefined;
      if (this._currentFullscreen) {
        node = this._rootDocContainer.getContentDocument()?.getRelatedNode(this._currentFullscreen);
        if (node) {
          node.setAttr('_fs_fullscreen', `${true}`);
          this._currentFullscreen = fullscreen;
        }
      }

      node = this._rootDocContainer.getContentDocument()?.getRelatedNode(fullscreen);
      if (node) {
        node.setAttr('_fs_fullscreen', `${true}`);
        this._currentFullscreen = fullscreen;
      }
    } else if (this._currentFullscreen) {
      const node = this._rootDocContainer
        .getContentDocument()
        ?.getRelatedNode(this._currentFullscreen);
      if (node) {
        node.setAttr('_fs_fullscreen', `${false}`);
        this._currentFullscreen = null;
      }
    }
  }

  /**
   * Convert a point in the coordinate system of a frame to a point in the
   * coordinate system of the page's viewport.
   *
   * @param framePoint - A point specified relative to some frame's coordinate
   * space.
   * @return the transformed point, or undefined if either 'framePoint' is
   * undefined or the frame it's associated with has no active document.
   */
  #framePointToPage = (framePoint: FramePoint | undefined): Point | undefined => {
    if (!framePoint) {
      return undefined;
    }
    const doc = this.#frameDocs.get(framePoint.frame);
    if (!doc) {
      return undefined;
    }
    return doc.toRootDocMatrix.transformPoint(framePoint.point);
  };

  private _playEventsFromDocumentTimeline(
    rootDoc: FsDocument,
    pageOffset: number,
    doc: FsDocument,
  ): void {
    const events = doc.events;

    // Document focus:
    const docElement = doc.documentElement;
    if (docElement) {
      const documentFocused = events.documentEventTimeline.documentFocused.at(pageOffset);
      if (documentFocused === true && !docElement.hasAttr('_fs_window_focus')) {
        docElement.setAttr('_fs_window_focus', 'true', /* skipRewrite = */ true);
      } else if (documentFocused === false && docElement.hasAttr('_fs_window_focus')) {
        docElement.setAttr('_fs_window_focus', null, /* skipRewrite = */ true);
      }
    }

    // Selection:
    const selection = events.documentEventTimeline.selection.at(pageOffset);
    doc.setSelection(
      selection?.startId,
      selection?.startOffset,
      selection?.endId,
      selection?.endOffset,
    );

    // Layout viewport:
    const layoutViewportPos = events.documentEventTimeline.layoutViewportPos;
    for (const [fsId, posForFsId] of layoutViewportPos.entries()) {
      const scrollNode = doc.byId[fsId];
      const pos = posForFsId.at(pageOffset);
      if (scrollNode && pos !== undefined) {
        this._scrollLayout(rootDoc, scrollNode, pos, pageOffset);
      }
    }

    const layoutViewportSize = events.documentEventTimeline.layoutViewportSize.at(pageOffset);
    if (layoutViewportSize !== undefined) {
      this._resizeLayoutViewport(rootDoc, doc, layoutViewportSize);
    }

    // Visual viewport:
    const visualOffset = events.documentEventTimeline.visualViewportOffset;
    for (const [fsId, offsetForFsId] of visualOffset.entries()) {
      const scrollNode = doc.byId[fsId];
      const offset = offsetForFsId.at(pageOffset);
      if (scrollNode && offset !== undefined) {
        this._scrollVisualOffset(rootDoc, scrollNode, offset, pageOffset);
      }
    }

    const visualViewportSize = events.documentEventTimeline.visualViewportSize.at(pageOffset);
    if (visualViewportSize !== undefined) {
      this._resizeVisualViewport(rootDoc, doc, visualViewportSize);
    }

    // KeyframeEffects
    const keyframeEffects = events.documentEventTimeline.keyframeEffects;
    for (const [id, entry] of keyframeEffects.entries()) {
      const targetId = entry.targetId.at(pageOffset);
      const target = typeof targetId === 'number' ? doc.byId[targetId] : null;
      const timing = entry.timing.at(pageOffset);
      const composite = entry.composite.at(pageOffset);
      const iterationComposite = entry.iterationComposite.at(pageOffset);
      const keyframes = entry.keyframes.at(pageOffset);
      const effect = doc.keyframeEffectById(id) ?? doc.createKeyframeEffect(id, null, null);

      // Apply the changes for this point in the time
      effect.composite = composite;
      effect.iterationComposite = iterationComposite;
      effect.target = target;
      effect.setKeyframes(keyframes);
      effect.updateTiming(timing);
    }

    // Animations
    const animations = events.documentEventTimeline.animations;
    for (const [id, entry] of animations.entries()) {
      const effectId = entry.effectId.at(pageOffset);
      const playbackRate = entry.playbackRate.at(pageOffset);
      const playState = doc.paused ? 'paused' : entry.playState.at(pageOffset);
      const animation = doc.animationById(id) ?? doc.createAnimation(id, null);

      // Apply the changes for this point in time
      animation.effect = doc.keyframeEffectById(effectId) ?? null;
      animation.updatePlaybackRate(playbackRate);
      animation.updatePlayState(playState);
    }

    const dialogs = events.documentEventTimeline.dialogs;
    for (const [id, entry] of dialogs.entries()) {
      const state = entry.at(pageOffset);
      const dialog = doc.dialogsById(id) ?? doc.createDialog(id);
      dialog.setState(state as FSDialogState);
    }

    // Pointer kind:
    const pointerKind = events.documentEventTimeline.pointerKind.at(pageOffset) ?? 'fine';
    doc.documentElement?.setAttr('_fs_pointer', pointerKind);

    // Color scheme kind:
    const colorSchemeKind =
      events.documentEventTimeline.colorSchemeKind.at(pageOffset) ?? 'no-preference';
    doc.documentElement?.setAttr('_fs_prefers_color_scheme', colorSchemeKind);
  }

  /**
   * ClientWidth + ClientHeight make up the Initial Containing Block (ICB) and shouldn't change
   * without a window resize event on desktop. Mobile can change the ICB when showing
   * the Virtual Keyboard. Importantly this value can be smaller than the Layout Viewport
   * due to dynamic navigation panels, scrollbars, or the Virual Keyboard.
   *
   * The Visual Viewport is the visual portion of a screen excluding on-screen keyboards,
   * areas outside of a pinch-zoom area, or any other on-screen artifact that doesn't scale
   * with the dimensions of a page (source MDN). fs.js tracks this via the Visual Viewport API
   * for modern browsers and uses heurstics for legacy browsers.
   *
   * The Layout Viewport is simply the window.innerHeight and window.innerWidth. The layout viewport
   * can be affected by the Virtual Keyboard as well depending on UA implementation because the spec
   * is not clear.
   *
   * The playback iframe should always be set to match the Layout Viewport. The ICB should be allowed
   * to overflow into the remaining space in the Layout Viewport. The ICB should be restricted by the
   * Visual Viewport alone, content shouldn't overflow the visual container.
   *
   * UAs (browsers) will resize the ICB, Layout Viewport, and Visual Viewport in various ways
   * when the dynamic navigation, scroll bars, or the Virtual Keyboard are shown. We should be adaptive
   * to these dynamic changes to the Layout Viewport. In the future playback should treat mobile viewports
   * to match the user experience of a device being fixed in size. This would fix the zooming in/out experience
   * we have today when the keyboard is shown etc.
   *
   * In the near future we will need to be resilent to Viewport Variants during capture and playback and render the
   * UX according to how the UA rendered the page. This will affect how the ICB, Layout Viewport, and Visual Viewport
   * will be sized relative to the dymanic content (nav, scrollbars, virtual keyboard etc).
   *
   * Specifications:
   * (Layout) Viewport Definition (CSS2 Specification) → https://drafts.csswg.org/css2/#viewport
   * Containing Block Definition (CSS2 Specification) → https://drafts.csswg.org/css2/#containing-block
   * Viewport Relative Lengths (CSS Values 4 Specification) → https://drafts.csswg.org/css-values-4/#viewport-relative-lengths
   * The Visual Viewport (MDN) → https://developer.mozilla.org/en-US/docs/Glossary/Visual_Viewport
   * The Visual Viewport API (CSSOM Specification) → https://drafts.csswg.org/cssom-view/#the-visualviewport-interface
   * Viewport Variants (CSS Values 4 Specification) → https://www.w3.org/TR/css-values-4/#viewport-variants
   * Virtual Keyboard Specification → https://www.w3.org/TR/virtual-keyboard/
   */
  private _resizeLayoutViewport(
    rootDoc: FsDocument,
    doc: FsDocument,
    layoutViewportSize: LayoutViewportSize,
  ): void {
    let { width, height } = layoutViewportSize;
    const { clientWidth, clientHeight } = layoutViewportSize;

    // See CH139406: This heuristic seems to break on older UAs. Ideally we only want to clamp the
    // viewport to the ICB for older UAs and use the this flag to pin users to one path or the other
    // as an override.
    if (!this._ctx.flags.features.AppPlaybackUseLayoutViewport) {
      // On Blink (and possibly other engines), absolutely positioned elements
      // can increase the layout viewport size beyond the bounds of the client
      // area, even if there are no scrollbars. This affects layout in that the
      // position of `position: fixed` elements may change, although for common
      // use cases this won't be visible since their position is clamped to the
      // client area.
      //
      // Unfortunately, this doesn't map well to the model we use during
      // playback, because we conflate the size of the playback iframe with the
      // size of the layout viewport. For now, we'll just clamp the layout
      // viewport as best we can to try to match the likely size of the window
      // during recording.  We can't just clamp to the client area because that
      // would prevent us from accurately playing back traditional scrollbars,
      // which usually exist outside the client area. The recorded window size
      // also can't be used reliably, because the user may resize the window
      // during the session.
      //
      // We'll apply two sets of bounds. First, we'll bound the layout viewport
      // size to a sane range. This prevents excessively large layout viewports
      // at page load or other times when the below bounds aren't enough, and it
      // compensates for buggy sessions that claim to contain layout viewports
      // with zero area.
      const widthBounds = {
        lower: clientWidth,
        upper: [this._windowSize.width, this._screenSize.width, clientWidth],
      };
      const heightBounds = {
        lower: clientHeight,
        upper: [this._windowSize.height, this._screenSize.height, clientHeight],
      };

      // Second, we'll try to prevent absolutely positioned elements and other
      // layout edge cases from growing the layout viewport. Since a window
      // resize or native UI changes like traditional scrollbars being added or
      // removed would affect the client area as well as the layout viewport, we
      // only permit the layout viewport to grow if the client area changed as
      // well. We still allow it to shrink so that if we *do* get in a bad
      // state, we can recover.
      if (
        doc.isViewportSet() &&
        doc.clientArea.width == clientWidth &&
        doc.clientArea.height == clientHeight
      ) {
        widthBounds.upper.push(doc.layoutViewport.width);
        heightBounds.upper.push(doc.layoutViewport.height);
      }

      // If there are traditional scrollbars, we know their size; it's
      // 'width - clientWidth', and likewise for height. Unfortunately, right
      // now we have no good way of simulating the scrollbars during playback.
      // Instead, we need to set the layout viewport to the recorded client area
      // (i.e., the space inside the scrollbars) to ensure that the layout is
      // the same, since browsers avoid classic scrollbars when laying out the
      // document. That has the unfortunate side effect of potentially breaking
      // media queries, which are evaluated against the full layout viewport
      // (including the scrollbars) on non-WebKit browsers.
      width = clientWidth;
      height = clientHeight;

      const clamp = (value: number, bounds: { lower: number; upper: number[] }): number => {
        const upperBound = Math.max(...bounds.upper);
        return Math.max(Math.min(value, upperBound), bounds.lower);
      };

      // Clamp according to the bounds we determined above.
      width = clamp(width, widthBounds);
      height = clamp(height, heightBounds);
    }

    // Size the playback iframe to the full layout viewport.
    doc.setLayoutViewportSize(width, height);
    doc.setClientArea(clientWidth, clientHeight);

    this.updateFrameSize(rootDoc, doc);
  }

  private _resizeVisualViewport(rootDoc: FsDocument, doc: FsDocument, size: Vec2D): void {
    doc.setVisualViewportSize(size.x, size.y);
    this.updateFrameSize(rootDoc, doc);
  }

  private updateFrameSize(rootDoc: FsDocument, doc: FsDocument) {
    if (!doc) {
      return;
    }
    if (doc === rootDoc) {
      const doDocResize = () => {
        // Historical note: We used to calculate "overscroll" here, to replay
        // the snap-back effect you can get on mobile when you try to scroll
        // past the scrollable area of the document. We removed this feature
        // because the heuristic for calculating it was not very reliable.

        // Include the offset between the visual and layout viewports. This
        // will only be non-zero on browsers where we can record layout
        // viewport information.
        const panX = doc.visualViewport.offsetLeft;
        const panY = doc.visualViewport.offsetTop;
        this._host.updateRootFrameSize(doc.layoutViewport, {
          width: doc.visualViewport.width,
          height: doc.visualViewport.height,
          offsetLeft: panX,
          offsetTop: panY,
        });
      };

      // If we rewind, we will call into this flow potentially without a buffered document.
      // But no events have been played. So we will depend on flush (which is called for docs both buffered and not)
      // To dispatch this pending resize after reifying the DOM.
      if (!doc.isViewportSet()) {
        doc.pendingResize = doDocResize;
      } else {
        doDocResize();
      }
    }
  }

  private _positionPointer(pos: Point): void {
    this._host.setUserFocus(pos);
    this._host.positionPointer(pos.x, pos.y);
  }

  private _touchDown(touchid: number, pos: Point): void {
    // Don't show touches during seeking.
    if (this._seeking) {
      return;
    }

    this._host.setUserFocus(pos);
    this._host.touchDown(touchid, pos.x, pos.y);
  }

  private _touchUp(touchid: number): void {
    this._host.touchUp(touchid);
  }

  private _scrollLayout(
    rootDoc: FsDocument,
    node: FsNode,
    layoutViewportPos: Point,
    when: number,
  ): void {
    if (!node) {
      return;
    }
    node.setScroll(layoutViewportPos.x, layoutViewportPos.y, when);

    // Keep track of the viewport positions for the root doc, so we can apply
    // overscroll and visual viewport scrolling.
    if (node !== rootDoc.body && node !== rootDoc.documentElement) {
      return;
    }
    rootDoc.setLayoutViewportPos(layoutViewportPos.x, layoutViewportPos.y, when);
    this.updateFrameSize(rootDoc, rootDoc);
  }

  private _scrollVisualOffset(
    rootDoc: FsDocument,
    node: FsNode,
    visualViewportOffset: Vec2D,
    when: number,
  ): void {
    if (!node) {
      return;
    }

    // Keep track of the viewport positions for the root doc, so we can apply
    // overscroll and visual viewport scrolling.
    if (node !== rootDoc.body && node !== rootDoc.documentElement) {
      console.warn(
        `Tried to set visual offset on non-scrollingElement: ${node.tag} with fsid ${node.fsid}`,
      );
      return;
    }
    rootDoc.setVisualViewportOffset(visualViewportOffset.x, visualViewportOffset.y, when);
    this.updateFrameSize(rootDoc, rootDoc);
  }

  async teardown() {
    this._abortController.abort();

    try {
      await this._playbackCommitted;
    } catch (_) {
      // Ignore the exception that aborting may generate. (Or any other
      // exception; we don't care since we're being torn down anyway.)
    }

    this._rootDocContainer.setContentDocument(undefined);
    await this._rootDocContainer.flush();
  }

  private async _playDocumentToTime(rootDoc: FsRootDocument, pageOffset: number): Promise<void> {
    // Update subdocuments concurrently.
    const that = this;
    await Promise.all(
      (function* (): IterableIterator<Promise<void>> {
        for (const doc of rootDoc.documentTree()) {
          // Play back raw events. Yield the promise indicating completion to
          // Promise.all.
          yield that._playDocumentEvents(pageOffset, doc);

          // Play back timeline events like scroll position changes.
          try {
            that._playEventsFromDocumentTimeline(rootDoc, pageOffset, doc);
          } catch (e) {
            console.log(`Unexpected exception playing interpolated document events: ${e}`);
          }

          // Apply any overrides to the document's behavior that may be active.
          that.#applyOverrides(doc);
        }
      })(),
    );

    // We need a special case for :focus and :hover timelines. They need
    // to be applied after the VDOM is set in all subdocuments, but before
    // it is flushed to the real DOM.
    //
    // Since these are page timelines, we only need to consider the root
    // doc.
    //
    // TODO(bendean): Clean this up by organizing the flush processes
    // into steps with more explicit dependencies.
    const hoverMode = this._pageEventTimeline.hoverMode.at(pageOffset) ?? 'hover';
    if (hoverMode === 'hover') {
      const nodePath = this._pageEventTimeline.hoveredElement.at(pageOffset);
      if (nodePath) {
        rootDoc.pseudoClassManager.hoverPath(nodePath);
      }
    }
    const focusedElPath = this._pageEventTimeline.focusedElement.at(pageOffset);
    if (!focusedElPath) {
      rootDoc.pseudoClassManager.blur();
    } else {
      rootDoc.pseudoClassManager.focusPath(focusedElPath);
    }

    // Flushing and other final steps.
    for (const doc of rootDoc.documentTree()) {
      this.#frameDocs.set(doc.path.toFramePath(), doc);
      await doc.flush();
      this._preloadResources(pageOffset, doc);
    }
  }

  // Plays all the events for a frame, from the current time until the given pageOffset.
  private async _playDocumentEvents(pageOffset: number, doc: FsDocument): Promise<void> {
    const events = doc.events;

    // Decide whether we're seeking or just playing normally. The heuristic is
    // "jumping backwards or more than [threshold] milliseconds forward".
    if (pageOffset < this._highWaterMark || pageOffset > this._highWaterMark + SEEK_THRESHOLD_MS) {
      this.beginSeek();
    }

    // Play events.
    while (true) {
      const nextEvent = events.events[doc.nextEventIndex];
      if (!nextEvent) {
        break;
      }
      if (nextEvent.When > pageOffset) {
        break;
      }

      try {
        this._playEvent(doc, nextEvent);
      } catch (e) {
        console.warn(`Unexpected exception playing event: ${e}`);
      }

      doc.nextEventIndex++;
    }

    // Update contained documents for nested frames.
    for (const frame of doc.byCategory['frame']) {
      const fsid = frame.fsid;
      if (!fsid) {
        continue;
      }
      frame.setContentDocument(events.documentEventTimeline.subdocument.get(fsid).at(pageOffset));
    }
  }

  private _preloadResources(pageOffset: number, doc: FsDocument): void {
    // Preload resources that'll be needed in the near future.
    const events = doc.events;
    const resourceFetches = events.documentEventTimeline.resourceFetches.at(
      pageOffset + PRELOAD_HORIZON_MS,
    );
    if (resourceFetches.length > doc.resourcesPreloadedSoFar) {
      doc.resourcesPreloadedSoFar = resourceFetches.length;
      for (const resourceFetch of resourceFetches) {
        this._preloader.preloadWithPriority('low', resourceFetch.href, resourceFetch.destination);
      }
    }
  }

  onRenderingMayChange(): void {
    // We need to make sure that scroll positions, style fixes, and so forth
    // stay in sync. Reset the idle time and restart rAF if needed.
    this._idleStartTimeMs = undefined;
    if (this._rafHandle === 0) {
      this._rafHandle = requestAnimationFrame(timeMs => this.onRAF(timeMs));
    }
  }

  onResourceLoadStart(href: string, destination: RequestDestination): void {
    console.debug(`Started loading resource: ${shorten(href)}`);
    this._preloader.notifyLoaded(href);
    this._host.onResourceLoadStart(href, destination);
  }

  onResourceLoaded(href: string, destination: RequestDestination, result: LoadResult): void {
    if (result === 'load') {
      console.debug(`Finished loading resource: ${shorten(href)}`);
    } else {
      console.debug(`Failed to load resource: ${shorten(href)}`);
    }
    this._host.onResourceLoaded(href, destination, result);
  }

  zeroCurrentScrollPosition() {
    this._rootDocContainer?.getContentDocument()?.zeroScrollPositions();
    this._rootDocContainer?.getContentDocument()?.syncScrollPositions();
  }

  invalidateRendererGeometryCaches(): void {
    this.activeFsRootDocument()?.invalidateGeometryCachesForAllDescendants();
  }

  queryPath(path: NodePath, opts?: ObjectQueryOpts): ObjectInfo | undefined {
    const node = this.activeFsRootDocument()?.getRelatedNode(path);
    if (!node) return undefined;
    return this.#summarizeNode(node, opts?.includeInnerText);
  }

  queryPoint(point: Point, opts?: ObjectQueryOpts): ObjectInfo[] {
    return (
      this.activeFsRootDocument()
        ?.elementsFromRendererPoint(point)
        ?.filter(node => node.tag !== 'html' && node.tag !== 'body')
        ?.map(node => this.#summarizeNode(node, opts?.includeInnerText))
        ?.filter(isNonNull) ?? []
    );
  }

  querySelector(selector: string, opts?: ObjectQueryOpts): ObjectInfo[] {
    return (
      this.activeFsRootDocument()
        ?.querySelectorInDescendants(selector)
        ?.map(node => this.#summarizeNode(node, opts?.includeInnerText))
        ?.filter(isNonNull) ?? []
    );
  }

  isDescendant(testPath: NodePath, basePath: NodePath): boolean {
    const testNode = this.activeFsRootDocument()?.getRelatedNode(testPath);
    const baseNode = this.activeFsRootDocument()?.getRelatedNode(basePath);
    if (!testNode || !baseNode) {
      return false;
    }
    for (let node = testNode.parent; node; node = node.parent) {
      if (node === baseNode) {
        return true;
      }
    }
    return false;
  }

  computeDescendants(collection: ObjectCollection) {
    for (const path of Object.keys(collection)) {
      // start with any node in the queue to traverse subtree
      const nodePath = NodePath.fromString(path);
      if (!nodePath) {
        continue;
      }
      const node = this.activeFsRootDocument()?.getRelatedNode(nodePath);
      // for any existing results, we won't reprocess the subtree
      // the following will add the necessary relationships and use existing result data as needed
      this.#computeDescendantsRecursive(node, 0, collection);
    }
  }

  #computeDescendantsRecursive(
    node: FsNode | undefined,
    depth: number,
    collection: ObjectCollection,
  ): ObjectPathDepth[] {
    if (!node || !node.path) {
      return [];
    }
    const pathString = node.path.asString;

    // we either use results from the existing computed object descendants
    // if exists, we do need to adjust the depth relative to the recursive depth (as the object descendants depth is relative to the object)
    const object = collection[pathString];
    if (object?.descendants) {
      return object.descendants.map(descendant => ({
        ...descendant,
        depth: depth + descendant.depth,
      }));
    }

    // or we handle the current node (if applicable) and recurse
    const descendants: ObjectPathDepth[] = [];
    node.forEachChild(childNode => {
      const childDepth = depth + 1;
      // need to add the child node path first if it is an applicable descendant
      if (childNode.path && collection[childNode.path.asString]) {
        descendants.push({ path: childNode.path.asString, depth: childDepth });
      }
      // then we can add its associated descendants
      descendants.push(...this.#computeDescendantsRecursive(childNode, childDepth, collection));
    });
    if (object) {
      // if the current object is in our collection, we should set the descendants at this point
      object.descendants = descendants.map(child => ({ ...child, depth: child.depth - depth }));
    }
    return descendants;
  }

  #summarizeNode = (node: FsNode, includeInnerText?: boolean): ObjectInfo | undefined => {
    if (!node.path) {
      return undefined;
    }
    const selector = node.getSelector();
    if (!selector) {
      return undefined;
    }
    return {
      path: node.path.toString(),
      platform: 'web',
      rect: node.getRendererBoundingRect(),
      selector,
      innerText: includeInnerText ? node.getInnerTextFs() : undefined,
    };
  };

  /**
   * Apply any active overrides to the given document.
   *
   * Overrides can be used to tweak the presentation of the captured page to
   * better suit different situations.
   */
  #applyOverrides(doc: FsDocument): void {
    const docElement = doc.documentElement;
    if (!docElement) {
      return;
    }

    docElement.setAttr('_fs_cursor', this.#cursorOverride, /* skipRewrite = */ true);
  }

  onPointerEvent(doc: FsDocument, nativeEvent: PointerEvent): void {
    switch (nativeEvent.type) {
      case 'pointerdown':
      case 'pointerup':
      case 'pointermove': {
        const rootDoc = this.activeFsRootDocument();
        if (!rootDoc) {
          return;
        }
        const rendererPoint = doc.toRendererMatrix.transformPoint({
          x: nativeEvent.clientX,
          y: nativeEvent.clientY,
        });

        this._host.onEvent({
          type: nativeEvent.type,
          altKey: nativeEvent.altKey,
          button: nativeEvent.button,
          buttons: nativeEvent.buttons,
          clientX: rendererPoint.x,
          clientY: rendererPoint.y,
          ctrlKey: nativeEvent.ctrlKey,
          isPrimary: nativeEvent.isPrimary,
          metaKey: nativeEvent.metaKey,
          pointerId: nativeEvent.pointerId,
          pointerType: nativeEvent.pointerType,
          screenX: nativeEvent.screenX,
          screenY: nativeEvent.screenY,
          shiftKey: nativeEvent.shiftKey,
          targets: [],
        });
        break;
      }

      default:
        break;
    }
  }

  onScrollEvent(doc: FsDocument, type: 'scrollstart' | 'scroll' | 'scrollend'): void {
    if (type === 'scrollstart' || type === 'scrollend') {
      this._host.onEvent({ type });
    }
    doc.invalidateGeometryCachesForAllDescendants();
    this._host.onObjectsMayHaveChanged();
  }

  async setCursorOverride(cursor: RendererCursor): Promise<void> {
    const newCursorOverride = cursor === 'captured' ? null : cursor;
    if (this.#cursorOverride === newCursorOverride) {
      return;
    }

    this.#cursorOverride = newCursorOverride;
    await this.playToTime(this._highWaterMark);
  }

  async setInteractive(interactive: boolean): Promise<void> {
    // If we're becoming interactive, keep things as they are.
    if (interactive) return;

    // We're becoming non-interactive. Flush scroll positions and the like to
    // ensure that the renderer is now displaying the state of the captured
    // page.
    this.onRenderingMayChange();
  }

  private onRAF(timeMs: number) {
    if (this._idleStartTimeMs === undefined) {
      // Content changed since the last frame. Restart the idle timer.
      this._idleStartTimeMs = timeMs;
    } else if (timeMs - this._idleStartTimeMs >= 1000) {
      // We've been idle for at least a second. Stop the rAF callback for now.
      // We'll start it up again if we play more events later.
      this._rafHandle = 0;
      return;
    }

    this._syncRenderingChanges();

    this._rafHandle = requestAnimationFrame(timeMs => this.onRAF(timeMs));
  }

  // Synchronize the playback rendering state (i.e., what the browser is
  // actually showing) with the state of the recorded document. We need to call
  // this function regularly to deal with rendering changes that happen
  // asynchronously in ways that are out of our control - for example, changes
  // that happen as a consequence of external resources loading.
  private _syncRenderingChanges() {
    // Grab the root document. (We may not have one very early on, before the
    // initial load event for the playback iframe arrives.)
    const doc = this._rootDocContainer.getContentDocument();
    if (!doc) {
      return;
    }

    // Synchronize the scroll position of all scrollable elements in this
    // document. We do this every frame because browsers can sometimes change
    // the scroll position in an asynchronous and unpredictable manner.
    doc.syncScrollPositions();

    // Update viewport information. It may be out of sync if external
    // resources (stylesheets, images) have loaded.
    this.updateFrameSize(doc, doc);

    // If there were any scroll position or viewport changes, element sizes and
    // positions may have changed.
    doc.invalidateGeometryCachesForAllDescendants();
    this._host.onObjectsMayHaveChanged();
  }
}

function ensureMasked(node: FsNode, value: string) {
  let inspectedNode = node;
  if (inspectedNode.isText()) {
    if (!inspectedNode.parent) {
      // we should never be in a case where we don't have a parent, unless we're root
      // but root should have no text anyway
      return maskText(value); // just fail closed
    }
    inspectedNode = inspectedNode.parent;
  }
  if (
    inspectedNode.hasAttr(AttrMasked) &&
    inspectedNode.attr(AttrMasked) === 'true' &&
    !wasMasked(value)
  ) {
    // we received a plaintext value change for a masked text node
    // redaction may have replaced the value with `•`s and the leak would have been stopped
    // so do not record an event if it was already removed by redaction
    if (!Array.from(value).every(c => c === '•' || c === ' ')) {
      FsEvent.recordCustomEventV2('PlaybackPrivacyLeakDetected', {
        nodeId: node.fsid,
      });
    }
    return maskText(value);
  }
  return value;
}

// Look up the "qualified sheet id" from the recording stream,
// representing a stylesheet that's either attached to the DOM
// or dynamically constructed, to get the appropriate sheet
// from the given document.
function getTargetCssSheet(
  doc: FsDocument,
  maybeQualifiedId: number | SheetQualifiedIdentity,
): FsCSSOMSheet {
  const [ownerKind, id]: SheetQualifiedIdentity =
    typeof maybeQualifiedId === 'number' ? [SheetOwner.Node, maybeQualifiedId] : maybeQualifiedId;

  let sheet: FsCSSOMSheet | undefined;
  if (ownerKind == SheetOwner.Node) {
    const node = doc.byId[id];
    if (node instanceof FsCSSOMElement) {
      sheet = node.sheet;
    } else {
      console.warn(`Invalid stylesheet owner node type: ${node}`);
    }
  } else if (ownerKind == SheetOwner.Sheet) {
    sheet = doc.styleSheetById(id);
  } else {
    throw assertExhaustive(ownerKind, `Unrecognized sheet owner kind: ${ownerKind}`);
  }
  if (!sheet) {
    throw new Error('Missing stylesheet!');
  }
  return sheet;
}

/**
 * Looks up a value in the text corpus and returns the replacement if found.
 * @param value The original value
 * @param textCorpus The text corpus dictionary
 * @returns The replacement value if found in the corpus, otherwise the original value
 */
function getTextCorpusReplacement(value: unknown, textCorpus?: Record<string, string>): string {
  const str = `${value}`;
  if (!textCorpus) return str;
  const key = str.trim();
  return textCorpus[key] || str;
}
